import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface HeroCarouselProps {
  slides: Movie[];
}

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);
  const total = slides.length;

  const goTo = useCallback((index: number) => {
    setCurrent((index + total) % total);
  }, [total]);

  const next = useCallback(() => goTo(current + 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1), [current, goTo]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrent((c) => (c + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [total]);

  if (total === 0) return null;
  const slide = slides[current];

  return (
    <section className="relative w-full overflow-hidden" aria-label="焦点内容轮播">
      {slides.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={index !== current}
        >
          <img
            src={item.backdrop || item.poster}
            alt={`${item.title}高清电影海报`}
            title={`${item.title}高清电影海报`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/35"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-background-50/90 via-transparent to-transparent"></div>

      <div className="relative mx-auto flex min-h-[460px] w-full max-w-7xl flex-col justify-center px-4 py-14 md:min-h-[560px] md:px-8 md:py-20">
        <div key={slide.id} className="max-w-2xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 font-label text-xs text-primary-950">
              <i className="ri-fire-line" aria-hidden="true"></i>
              焦点推荐
            </span>
            {slide.genres.map((g) => (
              <span
                key={g}
                className="whitespace-nowrap rounded-full bg-background-50/15 px-3 py-1 font-label text-xs text-background-100 backdrop-blur-sm"
              >
                {g}
              </span>
            ))}
          </div>

          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
            {slide.title}
          </h1>
          <p className="mt-3 font-heading text-base italic text-accent-300 md:text-lg">
            {slide.tagline}
          </p>
          <p className="mt-4 line-clamp-3 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {slide.excerpt}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-sm text-background-200">
            <span className="inline-flex items-center gap-1.5 text-accent-300">
              <i className="ri-star-fill" aria-hidden="true"></i>
              <span className="font-semibold">{slide.score.toFixed(1)}</span>
            </span>
            <span>{slide.year}</span>
            <span>{slide.region}</span>
            <span>{slide.duration}</span>
            <span>导演 {slide.director}</span>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 font-label text-sm font-medium text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" aria-hidden="true"></i>
              查看剧情详情
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-6 py-3 font-label text-sm text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
            >
              <i className="ri-movie-2-line text-lg" aria-hidden="true"></i>
              浏览全部电影
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 right-4 flex items-center gap-3 md:bottom-10 md:right-8">
          <button
            type="button"
            onClick={prev}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-background-100/30 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={next}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-background-100/30 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
          </button>
        </div>

        <div className="absolute bottom-8 left-4 flex items-center gap-2 md:left-8">
          {slides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => goTo(index)}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                index === current ? "w-8 bg-accent-500" : "w-3 bg-background-100/50"
              }`}
              aria-label={`切换到第 ${index + 1} 张`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}